/**
 * Campaign API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type {
  Campaign,
  CampaignListResponse,
  CampaignResponse,
  CampaignCreateInput,
  CampaignUpdateInput,
} from '../types';

interface ListCampaignsOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  status?: string;
}

class CampaignService {
  /**
   * List campaigns with pagination.
   */
  async listCampaigns(
    workspaceId: string,
    idToken: string,
    options: ListCampaignsOptions = {}
  ): Promise<CampaignListResponse> {
    const { page = 1, size = 10, sortBy = 'created_at', order = 'desc', search, status } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<CampaignListResponse>(`/campaigns`, {
      params: { skip, limit: size, sort_by: sortBy, order, search, status },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list campaigns');
    }

    return response.data;
  }

  /**
   * Get a single campaign by ID.
   */
  async getCampaign(campaignId: string, workspaceId: string, idToken: string): Promise<Campaign> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<CampaignResponse>(`/campaigns/${campaignId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get campaign');
    }

    return response.data.data;
  }

  /**
   * Create a new campaign.
   */
  async createCampaign(
    workspaceId: string,
    idToken: string,
    data: CampaignCreateInput
  ): Promise<Campaign> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<CampaignResponse>(`/campaigns`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create campaign');
    }

    return response.data.data;
  }

  /**
   * Update a campaign.
   */
  async updateCampaign(
    campaignId: string,
    workspaceId: string,
    idToken: string,
    data: CampaignUpdateInput
  ): Promise<Campaign> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<CampaignResponse>(`/campaigns/${campaignId}`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update campaign');
    }

    return response.data.data;
  }

  /**
   * Delete a campaign.
   */
  async deleteCampaign(campaignId: string, workspaceId: string, idToken: string): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/campaigns/${campaignId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

const campaignService = new CampaignService();
export default campaignService;
